import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { GuideList } from "@/components/GuideList";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function Guides() {
  const { t, language } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      api.get("/guides", { params: { q: query || undefined, category: cat } })
        .then(({ data }) => setGuides(data))
        .catch(() => setGuides([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [query, cat]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">{t("guides_title")}</h1>
        <p className="mt-1.5 text-slate-500">{t("guides_subtitle")}</p>

        <div className="relative mt-6">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search_guides")}
            data-testid="guides-search-input"
            className="min-h-[48px] pl-10"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="guides-filters">
          <FilterChip active={cat === "all"} onClick={() => setCat("all")} testId="filter-all">{t("all_categories")}</FilterChip>
          {categories.map((c) => (
            <FilterChip key={c.slug} active={cat === c.slug} onClick={() => setCat(c.slug)} testId={`filter-${c.slug}`}>
              {language === "hi" ? c.name_hi : c.name_en}
            </FilterChip>
          ))}
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-40 animate-pulse rounded-2xl border border-slate-200 bg-white" />)}
            </div>
          ) : guides.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center text-slate-500" data-testid="guides-empty">{t("no_guides")}</p>
          ) : (
            <GuideList guides={guides} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={`guides-${testId}`}
      className={`min-h-[38px] rounded-full border px-3.5 text-sm font-medium transition-colors ${active ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600 hover:border-primary/40"}`}
    >
      {children}
    </button>
  );
}
